import type { Query } from './query'
import type { TableFilter } from './table-filter'

interface AdvertiserTypeBreakdownValue {
  raw: number
  percentage: number
}

interface AdvertiserTypeBreakdownItem {
  advertisers: AdvertiserTypeBreakdownValue
  views: AdvertiserTypeBreakdownValue
}

interface AdvertiserTypeBreakdownDetails {
  competitors: AdvertiserTypeBreakdownItem
  partners: AdvertiserTypeBreakdownItem
  unwanted: AdvertiserTypeBreakdownItem
  uncategorized: AdvertiserTypeBreakdownItem
  networkEntities: AdvertiserTypeBreakdownItem
  intermediaries: AdvertiserTypeBreakdownItem
}

interface AdvertiserTypeBreakdownQuery {
  country: string
  startDate: string
  endDate: string
  brandID: string
  engine: string
}

export interface AdvertiserTypeBreakdown {
  breakdown: AdvertiserTypeBreakdownDetails
  query: AdvertiserTypeBreakdownQuery
}

interface TopAdvertiserRanking {
  ID: string
  domain: string
  position: number
  visibility: number
}
export interface TopAdvertisers {
  rank: TopAdvertiserRanking[]
  query: Query
}

export interface AdvertiserList {
  ID: string
  domain: string
}
export interface AdvertiserListQuery {
  brandID: string
  country: string
  engine: string
  dateStart: string
  dateEnd: string
}

export enum AdvertiserCategory {
  Official = 'official',
  Partner = 'partner',
  Competitor = 'competitor',
  Undesirable = 'undesirable',
  Uncategorized = 'uncategorized',
  NetworkEntity = 'network_entity',
  Intermediary = 'intermediary',
}

interface AdvertiserTag {
  ID: string
  label: string
}

export interface AdvertiserReportDTO {
  id: string
  domain: string
  visibility: number
  nbViews: number
  tags: AdvertiserTag[]
  category: AdvertiserCategory | null
}

export interface ReportsRequestDTO {
  brandID: string[] | string
  engine: string[] | string
  country: string[] | string
  date: {
    start: string
    end: string
  }
  filters: TableFilter
}

interface AdvertiserReportsMeta {
  page: {
    size: number
    total: number
  }
  request: ReportsRequestDTO
}

export interface AdvertiserReportsLinks {
  next?: string
  prev?: string
  exportCSV?: string
}

interface AdvertiserIntervalStatsDTO {
  date: string
  advertiserCount: number
  views: number
}

export interface AdvertiserEvolution {
  interval: 'daily' | 'weekly' | 'monthly'
  data: AdvertiserIntervalStatsDTO[]
}

export interface AdvertiserReportsResponse {
  data: AdvertiserReportDTO[]
  meta: AdvertiserReportsMeta
  links: AdvertiserReportsLinks
  evolution: AdvertiserEvolution
}

export interface AdvertiserReportsQuery {
  filters: TableFilter
  pages: {
    size: number
  }
  sort: {
    by: string
    direction: 'ASC' | 'DESC'
  }
}

export type AdvertiserReportBody = Omit<ReportsRequestDTO, 'filters'> & {
  filters?: TableFilter
}

export interface AddTagToAdvertiserDTO {
  brandID: string
  advertiserID: string
  tags: AdvertiserTag[]
}

interface SavedFilterUserContextDTO {
  brandID: string
  country: string
  engine: string
}

export interface AdvertiserSavedFilterDTO {
  ID: string
  ownerID: string
  userContext: SavedFilterUserContextDTO
  filters: TableFilter
  name: string
  favourite: boolean
}

export interface AdvertisementSavedFilterDTO {
  ID: string
  ownerID: string
  userContext: SavedFilterUserContextDTO
  filters: TableFilter
  name: string
  favourite: boolean
}

export interface CreateAdvertiserSavedFilterDTO {
  filters: TableFilter
  userContext: SavedFilterUserContextDTO
  name: string
}

export interface UpdateAdvertiserSavedFilterDTO {
  filters: TableFilter
  name: string
}

export interface CreateAdvertisementSavedFilterDTO {
  filters: TableFilter
  userContext: SavedFilterUserContextDTO
  name: string
}

export interface UpdateAdvertisementSavedFilterDTO {
  filters: TableFilter
  name: string
}

interface SummaryPercentDTO {
  nb: number
  percent: number
}

export interface SummaryResponseDTO {
  totalNb: number
  threshold: number
  below: SummaryPercentDTO
  above: SummaryPercentDTO
}

export interface BrandPresencePressure {
  nbKeywords: number
  nbKeywordsWithoutAds: number
  pressure: {
    nb: number
    percent: number
  }
  threshold: number
}

interface PressureBucket {
  nbAdvertisers: number
  nbPages: number
  percent: number
}

export interface PressureDTO {
  query: {
    startDate: string
    endDate: string
    country: string
    engine: string
    brandID: string
    keywordIDs?: string[]
  }
  totalResults: number
  buckets: PressureBucket[]
}

interface ByAdType {
  ads: number | null
  shopping: number | null
}

interface PositionEvolutionPoint {
  date: string
  /** @deprecated use averagePositionByType */
  averagePosition: number | null
  averagePositionByType: ByAdType
  nbViews: number
  nbViewsByType: ByAdType
  nbAds: number
}

interface PositionEvolutionAdvertiserItem {
  advertiser: {
    ID: string
    domain: string
    mainDomain: string
  }
  evolution: PositionEvolutionPoint[]
}

export interface PositionEvolutionDTO {
  interval: 'daily' | 'weekly' | 'monthly'
  advertisers: PositionEvolutionAdvertiserItem[]
}
